import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../supabase/config';
import { Input } from '../../components/admin/shared/Input';
import { Textarea } from '../../components/admin/shared/Textarea';
import { Button } from '../../components/admin/shared/Button';
import { useToast, ToastContainer } from '../../components/admin/shared/Toast';
import { Upload } from 'lucide-react';

const Profile = () => {
  const [profile, setProfile] = useState({
    name: '',
    title: '',
    bio: '',
    email: '',
    phone: '',
    location: '',
    github: '',
    linkedin: '',
    twitter: '',
    website: '',
    resumeUrl: '',
    image: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const { toasts, addToast } = useToast();

  const fetchProfile = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('portfolio')
        .select('*')
        .eq('id', 'profile')
        .single();

      if (error && error.code !== 'PGRST116') throw error;
      
      if (data) {
        setProfile({
          name: data.name || '',
          title: data.title || '',
          bio: data.bio || '',
          email: data.email || '',
          phone: data.phone || '',
          location: data.location || '',
          github: data.github || '',
          linkedin: data.linkedin || '',
          twitter: data.twitter || '',
          website: data.website || '',
          resumeUrl: data.resume_url || '',
          image: data.image || ''
        });
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
      addToast('Error loading profile', 'error');
    } finally {
      setLoading(false);
    }
  }, [addToast]);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  const handleImageUpload = async (file) => {
    setUploading(true);
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}.${fileExt}`;
      
      const { error: uploadError } = await supabase.storage
        .from('portfolio-images')
        .upload(fileName, file);
      
      if (uploadError) throw uploadError;
      
      const { data } = supabase.storage
        .from('portfolio-images')
        .getPublicUrl(fileName);
      
      setProfile({ ...profile, image: data.publicUrl });
      addToast('Image uploaded successfully', 'success');
    } catch (error) {
      console.error('Error uploading image:', error);
      addToast('Error uploading image', 'error');
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const { error } = await supabase
        .from('portfolio')
        .upsert({
          id: 'profile',
          name: profile.name,
          title: profile.title,
          bio: profile.bio,
          email: profile.email,
          phone: profile.phone,
          location: profile.location,
          github: profile.github,
          linkedin: profile.linkedin,
          twitter: profile.twitter,
          website: profile.website,
          resume_url: profile.resumeUrl,
          image: profile.image
        }, { onConflict: 'id' });

      if (error) throw error;
      addToast('Profile updated successfully!', 'success');
    } catch (error) {
      console.error('Error saving profile:', error);
      addToast('Error saving profile: ' + error.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="text-white">Loading...</div>;
  }

  return (
    <div>
      <ToastContainer toasts={toasts} />
      
      <h1 className="text-3xl font-bold text-white mb-8">Profile Information</h1>

      <div className="bg-slate-800 rounded-lg p-6 border border-slate-700 mb-6">
        <h2 className="text-xl font-bold text-white mb-4">Profile Picture</h2>
        <div className="flex items-center gap-6">
          {profile.image && (
            <img
              src={profile.image}
              alt="Profile"
              className="w-32 h-32 rounded-full object-cover"
            />
          )}
          <div>
            <label className="flex items-center gap-2 cursor-pointer">
              <Upload size={20} className="text-slate-400" />
              <span className="text-slate-400">Choose File</span>
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (file) handleImageUpload(file);
                }}
                disabled={uploading}
              />
            </label>
            {uploading && <p className="text-slate-400 text-sm mt-2">Uploading...</p>}
          </div>
        </div>
      </div>

      <div className="bg-slate-800 rounded-lg p-6 border border-slate-700 mb-6">
        <h2 className="text-xl font-bold text-white mb-4">Basic Information</h2>
        <Input
          label="Name"
          value={profile.name}
          onChange={(e) => setProfile({ ...profile, name: e.target.value })}
        />
        <Input
          label="Title"
          value={profile.title}
          onChange={(e) => setProfile({ ...profile, title: e.target.value })}
          placeholder="e.g., Software Engineer"
        />
        <Textarea
          label="Bio"
          value={profile.bio}
          onChange={(e) => setProfile({ ...profile, bio: e.target.value })}
          placeholder="Tell us about yourself..."
        />
      </div>

      <div className="bg-slate-800 rounded-lg p-6 border border-slate-700 mb-6">
        <h2 className="text-xl font-bold text-white mb-4">Contact</h2>
        <Input
          type="email"
          label="Email"
          value={profile.email}
          onChange={(e) => setProfile({ ...profile, email: e.target.value })}
          placeholder="cimanes.sj@gmail.com"
        />
        <Input
          label="Phone"
          value={profile.phone}
          onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
        />
        <Input
          label="Location"
          value={profile.location}
          onChange={(e) => setProfile({ ...profile, location: e.target.value })}
        />
      </div>

      <div className="bg-slate-800 rounded-lg p-6 border border-slate-700 mb-6">
        <h2 className="text-xl font-bold text-white mb-4">Social Links</h2>
        <Input
          label="GitHub"
          value={profile.github}
          onChange={(e) => setProfile({ ...profile, github: e.target.value })}
          placeholder="https://github.com/sanjie01"
        />
        <Input
          label="LinkedIn"
          value={profile.linkedin}
          onChange={(e) => setProfile({ ...profile, linkedin: e.target.value })}
          placeholder="https://www.linkedin.com/in/sonjay-cimanes-2966b1372"
        />
        <Input
          label="Twitter"
          value={profile.twitter}
          onChange={(e) => setProfile({ ...profile, twitter: e.target.value })}
          placeholder="https://twitter.com/yourhandle"
        />
        <Input
          label="Website"
          value={profile.website}
          onChange={(e) => setProfile({ ...profile, website: e.target.value })}
          placeholder="https://yourwebsite.com"
        />
        <Input
          label="Resume URL"
          value={profile.resumeUrl}
          onChange={(e) => setProfile({ ...profile, resumeUrl: e.target.value })}
          placeholder="/Sonjay_Cimanes_Resume.pdf"
        />
      </div>

      <div className="flex gap-4">
        <Button
          variant="primary"
          onClick={handleSave}
          loading={saving}
        >
          Save Changes
        </Button>
      </div>
    </div>
  );
};

export default Profile;

